import { useMutation } from "@tanstack/react-query";
import axios from "axios";

// Direct API call to download the Excel template
const downloadTemplateFile = async (params: {
  selectedClass?: { name: string; section?: string | null } | null;
  selectedSession?: { name: string } | null;
} = {}): Promise<{ filename: string; success: boolean }> => {
  
  try {
    const apiUrl = `${process.env.REACT_APP_API_URL}/templates/bulk-application-template`;
    const className = params.selectedClass?.name
      ? `${params.selectedClass.name}-${params.selectedClass.section || "ALL"}`
      : undefined;
    
    // Make API call to the backend
    const response = await axios.get(apiUrl, {
      params: className ? { className } : undefined,
      responseType: 'blob',
      headers: {
        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'Content-Type': 'application/json',
      },
      timeout: 10000, // 10 second timeout
    });

    // Check if response is valid
    if (!response.data) {
      throw new Error('No data received from API');
    }

    if (response.data.size === 0) {
      throw new Error('Empty file received from API');
    }

    // Create blob from response data
    const blob = new Blob([response.data], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });

    if (blob.size === 0) {
      throw new Error('Created blob is empty');
    }

    // Create filename with class and session info if available
    let filename = 'Bulk Application Upload Template.xlsx';
    if (params.selectedClass?.name || params.selectedSession?.name) {
      const classPart = params.selectedClass?.name || 'Class';
      const sessionPart = params.selectedSession?.name || 'Session';
      filename = `Bulk Application Upload Template - ${classPart} - ${sessionPart}.xlsx`;
    }

    // Create download link and trigger download
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up the URL
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 100);


    return { filename, success: true };
  } catch (error: any) {
    // Provide more specific error messages
    if (error.code === 'ECONNREFUSED') {
      throw new Error('Cannot connect to server. Please check if the backend is running.');
    } else if (error.response?.status === 404) {
      throw new Error('Template file not found on server.');
    } else if (error.response?.status === 500) {
      throw new Error('Server error occurred while generating template.');
    } else if (error.message.includes('timeout')) {
      throw new Error('Request timed out. Please try again.');
    } else {
      throw new Error(`Download failed: ${error.message}`);
    }
  }
};

// Fallback function to download from public folder
const downloadFromPublicFolder = async (params: {
  selectedClass?: { name: string; section?: string | null } | null;
  selectedSession?: { name: string } | null;
} = {}): Promise<{ filename: string; success: boolean }> => {
  
  try {
    // Create filename with class and session info if available
    let filename = 'Bulk Application Upload Template.xlsx';
    if (params.selectedClass?.name || params.selectedSession?.name) {
      const classPart = params.selectedClass?.name || 'Class';
      const sessionPart = params.selectedSession?.name || 'Session';
      filename = `Bulk Application Upload Template - ${classPart} - ${sessionPart}.xlsx`;
    }

    // Create download link for public folder file
    const link = document.createElement('a');
    link.href = '/Bulk Application Upload Template.xlsx';
    link.download = filename;
    link.style.display = 'none';
    
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    return { filename, success: true };
  } catch (error) {
    throw error;
  }
};

export function useTemplate() {
  const downloadTemplateMutation = useMutation({
    mutationFn: downloadTemplateFile,
    onSuccess: (data) => {
    },
    onError: (error) => {
    },
  });

  const downloadBulkApplicationTemplate = async (params: {
    selectedClass?: { name: string; section?: string | null } | null;
    selectedSession?: { name: string } | null;
  } = {}): Promise<{ filename: string; success: boolean }> => {
    
    try {
      // Try API first
      return await downloadTemplateMutation.mutateAsync(params);
    } catch (apiError) {
      
      try {
        // Try fallback to public folder
        return await downloadFromPublicFolder(params);
      } catch (fallbackError) {
        throw new Error('Template download failed. Please check if the backend is running and the template file exists.');
      }
    }
  };

  return {
    downloadBulkApplicationTemplate,
    downloadTemplateMutation,
  };
}